import { useAuth0 } from '@auth0/auth0-react';
import { config } from '@config';
import { errorToast } from '@hooks/toast.hook';
import axios, { AxiosError, Method } from 'axios';
import { getSelectedCompanyId } from '../utils/company.util';

type ProcessApiParams = {
  body?: unknown;
  extraHeaders?: Record<string, string>;
  method: Method;
  path: string;
  query?: Record<string, unknown>;
  signal?: AbortSignal;
};

type ApiParams = Omit<ProcessApiParams, 'method'>;
type ApiGetParams = Omit<ApiParams, 'body'>;

type ApiError = {
  message: string;
  statusCode: number;
};

type ErrorResponse = {
  error: ApiError;
};

export class ApiErrorWrapper extends Error {
  errors: ApiError[];
  originalError?: Error;

  constructor(params: { errors: ApiError[]; originalError?: Error }) {
    super(params.errors[0].message);
    this.errors = params.errors;
    this.originalError = params.originalError;
  }
}

const axiosInstance = axios.create({
  baseURL: config.api.baseUrl,
  withCredentials: true,
});

export function isCanceledApiError(error: unknown): boolean {
  return axios.isCancel(error);
}

export const useBuildHeaders = () => {
  const { getAccessTokenSilently, getIdTokenClaims } = useAuth0();
  const buildHeaders = async () => {
    const [accessToken, idTokenClaims] = await Promise.all([getAccessTokenSilently(), getIdTokenClaims()]);

    const selectedCompanyId = getSelectedCompanyId();

    return {
      Authorization: `Bearer ${accessToken}`,
      ...(selectedCompanyId && { 'x-az-company-id': selectedCompanyId }),
      ...(idTokenClaims && { 'x-az-user-data': idTokenClaims.__raw }),
    };
  };

  return { buildHeaders };
};
type UseApiArgs = {
  showToastOnError?: boolean;
};

export const useApi = (args?: UseApiArgs) => {
  const { showToastOnError = true } = args ?? {};
  const { buildHeaders } = useBuildHeaders();

  const handleApiErrorToast = (error: ApiErrorWrapper) => {
    console.error('API Error:', error);
    const message = error.errors?.[0]?.message ?? 'An error occurred.';

    if (showToastOnError) {
      errorToast({ description: message });
    }
  };

  async function processApiRequest(params: ProcessApiParams) {
    const { body, extraHeaders, method, path, query, signal } = params;
    const headers = { ...extraHeaders, ...(await buildHeaders()) };

    try {
      const response = await axiosInstance.request({
        data: body,
        headers,
        method,
        params: query,
        signal,
        url: path,
      });
      return response.data;
    } catch (error) {
      if (isCanceledApiError(error)) {
        throw error;
      }

      const axiosError = error as AxiosError<ErrorResponse>;

      const apiError: ApiError = axiosError.response?.data?.error ?? {
        message: 'Something went wrong. Please try again later.',
        statusCode: Number.parseInt(axiosError.code ?? '500', 10),
      };

      const apiErrorWrapper = new ApiErrorWrapper({ errors: [apiError], originalError: error as Error });
      handleApiErrorToast(apiErrorWrapper);
      throw apiErrorWrapper;
    }
  }

  async function apiDelete<T>(params: ApiParams): Promise<T> {
    return await processApiRequest({ ...params, method: 'DELETE' });
  }

  async function apiGet<T>(params: ApiGetParams): Promise<T> {
    return await processApiRequest({ ...params, method: 'GET' });
  }

  async function apiPatch<T>(params: ApiParams): Promise<T> {
    return await processApiRequest({ ...params, method: 'PATCH' });
  }

  async function apiPost<T>(params: ApiParams): Promise<T> {
    return await processApiRequest({ ...params, method: 'POST' });
  }

  async function apiPut<T>(params: ApiParams): Promise<T> {
    return await processApiRequest({ ...params, method: 'PUT' });
  }

  return { apiDelete, apiGet, apiPatch, apiPost, apiPut };
};
